/**
 * Visits API
 * CRUD operations for visit management
 */

import apiClient from '@/lib/api-client'
import type {
  Visit,
  VisitCreate,
  VisitUpdate,
  VisitFilters,
  VisitListItem,
  VisitClassification,
  VisitClassificationCreate,
  VisitTransaction,
  VisitTransactionCreate,
  VisitTransactionUpdate,
} from '@/types/visit'
import type { PaginatedResponse, PaginationParams } from '@/types/common'

export const visitsApi = {
  /**
   * Get paginated list of visits
   */
  async getAll(
    filters?: VisitFilters,
    pagination?: PaginationParams
  ): Promise<PaginatedResponse<VisitListItem>> {
    const params = { ...filters, ...pagination }
    const response = await apiClient.get<PaginatedResponse<VisitListItem>>(
      '/visits',
      { params }
    )
    return response.data
  },

  /**
   * Get visit by ID
   */
  async getById(id: string): Promise<Visit> {
    const response = await apiClient.get<Visit>(`/visits/${id}`)
    return response.data
  },

  /**
   * Create new visit
   */
  async create(data: VisitCreate): Promise<Visit> {
    const response = await apiClient.post<Visit>('/visits', data)
    return response.data
  },

  /**
   * Update existing visit
   */
  async update(id: string, data: VisitUpdate): Promise<Visit> {
    const response = await apiClient.put<Visit>(`/visits/${id}`, data)
    return response.data
  },

  /**
   * Delete visit
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/visits/${id}`)
  },

  /**
   * Get all visits for a specific client
   */
  async getClientVisits(clientId: string): Promise<VisitListItem[]> {
    const response = await apiClient.get<VisitListItem[]>(`/visits/client/${clientId}`)
    return response.data
  },

  /**
   * Create or update visit classification
   */
  async createClassification(data: VisitClassificationCreate): Promise<VisitClassification> {
    const response = await apiClient.post<VisitClassification>('/visits/classifications', data)
    return response.data
  },

  /**
   * Get visit classification by visit ID
   */
  async getClassification(visitId: string): Promise<VisitClassification> {
    const response = await apiClient.get<VisitClassification>(`/visits/classifications/${visitId}`)
    return response.data
  },

  /**
   * Create visit transaction (assistance or denial)
   */
  async createTransaction(data: VisitTransactionCreate): Promise<VisitTransaction> {
    const response = await apiClient.post<VisitTransaction>('/visits/transactions', data)
    return response.data
  },

  /**
   * Get all transactions for a visit
   */
  async getTransactions(visitId: string): Promise<VisitTransaction[]> {
    const response = await apiClient.get<VisitTransaction[]>(`/visits/transactions/${visitId}`)
    return response.data
  },

  /**
   * Update an existing visit transaction (assistance details)
   */
  async updateTransaction(id: string, data: VisitTransactionUpdate): Promise<VisitTransaction> {
    const response = await apiClient.put<VisitTransaction>(`/visits/transactions/${id}`, data)
    return response.data
  },
}

export default visitsApi
